import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { adminApi, type SystemSetting } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Save,
  Check,
  Plus,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

// ═══════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════

interface VolumeDiscount {
  threshold: number;
  discountPercent: number;
}

interface Campaign {
  code: string;
  discountPercent: number;
  validUntil: string;
  maxUses: number | null;
  usedCount: number;
  active: boolean;
  duration: "forever" | "repeating";
  durationInMonths: number | null;
}

interface BureauTier {
  name: string;
  maxCompanies: number | null;
  annualFee: number;
  pricePerCompany: number;
}

type SectionStatus = "idle" | "saving" | "saved" | "error";

// ═══════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════

export function AdminPricing() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Pricing data
  const [basePrice, setBasePrice] = useState(2495);
  const [vatRate, setVatRate] = useState(0.25);
  const [volumeDiscounts, setVolumeDiscounts] = useState<VolumeDiscount[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [bureauTiers, setBureauTiers] = useState<BureauTier[]>([]);

  // Section status
  const [basicStatus, setBasicStatus] = useState<SectionStatus>("idle");
  const [volumeStatus, setVolumeStatus] = useState<SectionStatus>("idle");
  const [campaignStatus, setCampaignStatus] = useState<SectionStatus>("idle");
  const [bureauStatus, setBureauStatus] = useState<SectionStatus>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await adminApi.getSettings();
      if (response.success && response.data) {
        const settingsMap = new Map<string, SystemSetting>();
        for (const s of response.data) settingsMap.set(s.key, s);

        const bp = settingsMap.get("base_price");
        if (bp) setBasePrice(Number(bp.value) || 2495);

        const vr = settingsMap.get("vat_rate");
        if (vr) setVatRate(Number(vr.value) || 0.25);

        const vd = settingsMap.get("volume_discounts");
        if (vd && Array.isArray(vd.value)) setVolumeDiscounts(vd.value as VolumeDiscount[]);

        const ca = settingsMap.get("campaigns");
        if (ca && Array.isArray(ca.value)) setCampaigns(ca.value as Campaign[]);

        const bt = settingsMap.get("bureau_tiers");
        if (bt && Array.isArray(bt.value)) setBureauTiers(bt.value as BureauTier[]);
      } else {
        setLoadError(response.error?.message || "Kunde inte ladda prissättning");
      }
    } catch {
      setLoadError("Kunde inte ansluta till servern");
      toast.error("Kunde inte ladda prissättning");
    } finally {
      setIsLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // Save helpers
  // ═══════════════════════════════════════════════════════════════════

  const saveSetting = useCallback(async (key: string, value: unknown): Promise<boolean> => {
    const res = await adminApi.updateSetting(key, value);
    return res.success === true;
  }, []);

  const withStatus = useCallback(
    async (
      setter: (s: SectionStatus) => void,
      fn: () => Promise<boolean>
    ) => {
      setter("saving");
      setErrorMsg(null);
      try {
        const ok = await fn();
        if (ok) {
          setter("saved");
          toast.success("Inställningen har sparats");
          setTimeout(() => setter("idle"), 2000);
        } else {
          setter("error");
          setErrorMsg("Kunde inte spara");
          toast.error("Kunde inte spara inställningen");
        }
      } catch {
        setter("error");
        setErrorMsg("Kunde inte ansluta till servern");
        toast.error("Kunde inte ansluta till servern");
      }
    },
    []
  );

  // ═══════════════════════════════════════════════════════════════════
  // Section: Grundprissättning
  // ═══════════════════════════════════════════════════════════════════

  const handleSaveBasic = () =>
    withStatus(setBasicStatus, async () => {
      if (basePrice < 0 || basePrice > 100000) {
        setErrorMsg("Grundpris måste vara mellan 0 och 100 000 kr");
        return false;
      }
      if (vatRate < 0 || vatRate > 1) {
        setErrorMsg("Momssats måste vara mellan 0 och 1");
        return false;
      }
      const [a, b] = await Promise.all([
        saveSetting("base_price", basePrice),
        saveSetting("vat_rate", vatRate),
      ]);
      return a && b;
    });

  // ═══════════════════════════════════════════════════════════════════
  // Section: Volymrabatter
  // ═══════════════════════════════════════════════════════════════════

  const addVolumeDiscount = () => {
    setVolumeDiscounts((prev) => [
      ...prev,
      { threshold: prev.length > 0 ? prev[prev.length - 1].threshold + 5 : 5, discountPercent: 5 },
    ]);
  };

  const removeVolumeDiscount = (index: number) => {
    setVolumeDiscounts((prev) => prev.filter((_, i) => i !== index));
  };

  const updateVolumeDiscount = (index: number, field: keyof VolumeDiscount, value: number) => {
    setVolumeDiscounts((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [field]: value } : d))
    );
  };

  const handleSaveVolume = () =>
    withStatus(setVolumeStatus, async () => {
      // Validate
      for (const d of volumeDiscounts) {
        if (d.threshold < 1) {
          setErrorMsg("Tröskel måste vara minst 1");
          return false;
        }
        if (d.discountPercent < 0 || d.discountPercent > 100) {
          setErrorMsg("Rabatt måste vara mellan 0 och 100 %");
          return false;
        }
      }
      const sorted = [...volumeDiscounts].sort((a, b) => a.threshold - b.threshold);
      // Check for duplicate thresholds
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].threshold === sorted[i - 1].threshold) {
          setErrorMsg(`Duplicerade tröskelvärden: ${sorted[i].threshold}`);
          return false;
        }
      }
      setVolumeDiscounts(sorted);
      return saveSetting("volume_discounts", sorted);
    });

  // ═══════════════════════════════════════════════════════════════════
  // Section: Kampanjkoder
  // ═══════════════════════════════════════════════════════════════════

  const addCampaign = () => {
    setCampaigns((prev) => [
      ...prev,
      {
        code: "",
        discountPercent: 10,
        validUntil: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
        maxUses: null,
        usedCount: 0,
        active: true,
        duration: "forever",
        durationInMonths: null,
      },
    ]);
  };

  const removeCampaign = (index: number) => {
    setCampaigns((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCampaign = (index: number, field: keyof Campaign, value: unknown) => {
    setCampaigns((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const handleSaveCampaigns = () =>
    withStatus(setCampaignStatus, async () => {
      for (const c of campaigns) {
        if (!c.code.trim()) {
          setErrorMsg("Kampanjkod får inte vara tom");
          return false;
        }
        if (c.discountPercent < 0 || c.discountPercent > 100) {
          setErrorMsg(`Ogiltig rabatt för kampanj "${c.code}": måste vara 0–100 %`);
          return false;
        }
        if (!c.validUntil) {
          setErrorMsg(`Kampanj "${c.code}" saknar giltighetsdatum`);
          return false;
        }
        if (c.duration === "repeating" && (!c.durationInMonths || c.durationInMonths < 12 || c.durationInMonths > 120)) {
          setErrorMsg(`Kampanj "${c.code}": antal år måste vara mellan 1 och 10`);
          return false;
        }
      }
      // Check for duplicate codes
      const codes = campaigns.map((c) => c.code.trim().toUpperCase());
      const uniqueCodes = new Set(codes);
      if (codes.length !== uniqueCodes.size) {
        setErrorMsg("Det finns duplicerade kampanjkoder");
        return false;
      }
      return saveSetting("campaigns", campaigns);
    });

  // ═══════════════════════════════════════════════════════════════════
  // Section: Byråprissättning
  // ═══════════════════════════════════════════════════════════════════

  const addBureauTier = () => {
    setBureauTiers((prev) => [
      ...prev,
      { name: "", maxCompanies: null, annualFee: 0, pricePerCompany: 0 },
    ]);
  };

  const removeBureauTier = (index: number) => {
    setBureauTiers((prev) => prev.filter((_, i) => i !== index));
  };

  const updateBureauTier = (index: number, field: keyof BureauTier, value: unknown) => {
    setBureauTiers((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t))
    );
  };

  const handleSaveBureau = () =>
    withStatus(setBureauStatus, async () => {
      for (const t of bureauTiers) {
        if (!t.name.trim()) {
          setErrorMsg("Alla byråtariffer måste ha ett namn");
          return false;
        }
        if (t.annualFee < 0) {
          setErrorMsg(`Ogiltig årsavgift för "${t.name}"`);
          return false;
        }
        if (t.pricePerCompany < 0) {
          setErrorMsg(`Ogiltigt pris/företag för "${t.name}"`);
          return false;
        }
      }
      // Check for duplicate names
      const names = bureauTiers.map((t) => t.name.trim().toLowerCase());
      const uniqueNames = new Set(names);
      if (names.length !== uniqueNames.size) {
        setErrorMsg("Det finns duplicerade tariffnamn");
        return false;
      }
      return saveSetting("bureau_tiers", bureauTiers);
    });

  // ═══════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{loadError}</p>
        <button onClick={loadPricing} className="mt-3 text-sm text-red-600 hover:underline">
          Försök igen
        </button>
      </div>
    );
  }

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.pricing.title} noIndex />
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Prissättning</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Hantera priser, rabatter och kampanjer</p>
        </div>

      {errorMsg && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle size={16} className="text-red-600 shrink-0" />
          <p className="text-sm text-red-700">{errorMsg}</p>
        </div>
      )}

      {/* 1. Grundprissättning */}
      <section className="card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Grundprissättning</h2>
            <p className="text-sm text-gray-500 mt-0.5">Pris per företag och räkenskapsår</p>
          </div>
          <SaveButton status={basicStatus} onClick={handleSaveBasic} className="w-full sm:w-auto" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grundpris (SEK exkl moms)</label>
            <input
              type="number"
              value={basePrice}
              onChange={(e) => setBasePrice(Number(e.target.value))}
              min={0}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Momssats</label>
            <input
              type="number"
              value={vatRate}
              onChange={(e) => setVatRate(Number(e.target.value))}
              min={0}
              max={1}
              step={0.01}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pris inkl moms</label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-900">
              {Math.round(basePrice * (1 + vatRate)).toLocaleString("sv-SE")} SEK
            </div>
          </div>
        </div>
      </section>

      {/* 2. Volymrabatter */}
      <section className="card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Volymrabatter</h2>
            <p className="text-sm text-gray-500 mt-0.5">Rabatt baserad på antal företag</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={addVolumeDiscount} className="flex-1 sm:flex-initial">
              <Plus size={14} /> Lägg till
            </Button>
            <SaveButton status={volumeStatus} onClick={handleSaveVolume} className="flex-1 sm:flex-initial" />
          </div>
        </div>

        {volumeDiscounts.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">Inga volymrabatter konfigurerade</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-600">Antal företag (från)</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-600">Rabatt (%)</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-600">Effektivt pris</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {volumeDiscounts.map((d, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-2 pr-4">
                      <input
                        type="number"
                        value={d.threshold}
                        onChange={(e) => updateVolumeDiscount(idx, "threshold", Number(e.target.value))}
                        min={1}
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="number"
                        value={d.discountPercent}
                        onChange={(e) => updateVolumeDiscount(idx, "discountPercent", Number(e.target.value))}
                        min={0}
                        max={100}
                        className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 pr-4 text-gray-500">
                      {Math.round(basePrice * (1 - d.discountPercent / 100)).toLocaleString("sv-SE")} SEK
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => removeVolumeDiscount(idx)}
                        className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 3. Kampanjkoder */}
      <section className="card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Kampanjkoder</h2>
            <p className="text-sm text-gray-500 mt-0.5">Rabattkoder för kunder</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={addCampaign} className="flex-1 sm:flex-initial">
              <Plus size={14} /> Ny kampanj
            </Button>
            <SaveButton status={campaignStatus} onClick={handleSaveCampaigns} className="flex-1 sm:flex-initial" />
          </div>
        </div>

        {campaigns.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">Inga kampanjer konfigurerade</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Kod</th>
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Rabatt (%)</th>
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Giltig t.o.m.</th>
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Max</th>
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Använda</th>
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Varaktighet</th>
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Aktiv</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-2 pr-3">
                      <input
                        type="text"
                        value={c.code}
                        onChange={(e) => updateCampaign(idx, "code", e.target.value.toUpperCase())}
                        placeholder="RABATT10"
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        value={c.discountPercent}
                        onChange={(e) => updateCampaign(idx, "discountPercent", Number(e.target.value))}
                        min={0}
                        max={100}
                        className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="date"
                        value={c.validUntil}
                        onChange={(e) => updateCampaign(idx, "validUntil", e.target.value)}
                        className="px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        value={c.maxUses ?? ""}
                        onChange={(e) =>
                          updateCampaign(idx, "maxUses", e.target.value === "" ? null : Number(e.target.value))
                        }
                        placeholder="Obegr."
                        min={0}
                        className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <span className="text-gray-500 px-2">{c.usedCount}</span>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-1.5">
                        <select
                          value={c.duration || "forever"}
                          onChange={(e) => {
                            const val = e.target.value as "forever" | "repeating";
                            updateCampaign(idx, "duration", val);
                            if (val === "forever") {
                              updateCampaign(idx, "durationInMonths", null);
                            } else if (!c.durationInMonths) {
                              updateCampaign(idx, "durationInMonths", 12);
                            }
                          }}
                          className="px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="forever">För alltid</option>
                          <option value="repeating">Antal år</option>
                        </select>
                        {c.duration === "repeating" && (
                          <input
                            type="number"
                            value={c.durationInMonths ? c.durationInMonths / 12 : 1}
                            onChange={(e) => {
                              const years = Math.max(1, Math.min(10, Number(e.target.value) || 1));
                              updateCampaign(idx, "durationInMonths", years * 12);
                            }}
                            min={1}
                            max={10}
                            className="w-14 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            title="Antal år"
                          />
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={c.active}
                          onChange={(e) => updateCampaign(idx, "active", e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600" />
                      </label>
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => removeCampaign(idx)}
                        className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 4. Byråprissättning */}
      <section className="card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Byråprissättning</h2>
            <p className="text-sm text-gray-500 mt-0.5">Prisnivåer för redovisningsbyråer</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={addBureauTier} className="flex-1 sm:flex-initial">
              <Plus size={14} /> Lägg till
            </Button>
            <SaveButton status={bureauStatus} onClick={handleSaveBureau} className="flex-1 sm:flex-initial" />
          </div>
        </div>

        {bureauTiers.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">Inga byråtariffer konfigurerade</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Nivå</th>
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Max företag</th>
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Årsavgift (SEK)</th>
                  <th className="text-left py-2 pr-3 font-medium text-gray-600">Pris/företag (SEK)</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {bureauTiers.map((t, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-2 pr-3">
                      <input
                        type="text"
                        value={t.name}
                        onChange={(e) => updateBureauTier(idx, "name", e.target.value)}
                        placeholder="T.ex. Start, Pro, Enterprise"
                        className="w-40 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        value={t.maxCompanies ?? ""}
                        onChange={(e) =>
                          updateBureauTier(idx, "maxCompanies", e.target.value === "" ? null : Number(e.target.value))
                        }
                        placeholder="Obegr."
                        min={0}
                        className="w-24 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        value={t.annualFee}
                        onChange={(e) => updateBureauTier(idx, "annualFee", Number(e.target.value))}
                        min={0}
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        value={t.pricePerCompany}
                        onChange={(e) => updateBureauTier(idx, "pricePerCompany", Number(e.target.value))}
                        min={0}
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => removeBureauTier(idx)}
                        className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SaveButton component
// ═══════════════════════════════════════════════════════════════════

function SaveButton({ status, onClick, className }: { status: SectionStatus; onClick: () => void; className?: string }) {
  return (
    <Button
      onClick={onClick}
      disabled={status === "saving"}
      size="sm"
      className={className}
    >
      {status === "saving" ? (
        <Spinner size="sm" />
      ) : status === "saved" ? (
        <>
          <Check size={14} className="text-white" />
          Sparat
        </>
      ) : (
        <>
          <Save size={14} />
          Spara
        </>
      )}
    </Button>
  );
}
